import React from 'react';
import ReactModal from 'react-modal';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Hammer from 'hammerjs';
import _ from 'lodash';
import { SvgIcon } from 'core/icons';
import { ModalHeader } from '../common/modal/Modal';
import LoadImage from '../common/images/LoadImage';
import { pan, pinch, zoomToCenter } from './imageZoomHelpers';
import { normalize } from '../common/cgi/cgi';

const getImageDimensions = image => [image.naturalWidth, image.naturalHeight];
const getWindowDimensions = window => [window.innerWidth, window.innerHeight];

const computeDefaults = ({ image, window }) => {
  const [windowWidth, windowHeight] = getWindowDimensions(window);
  const [imageWidth, imageHeight] = getImageDimensions(image);

  const isZoomDisabled =
    imageWidth <= windowWidth && imageHeight < windowHeight;

  return {
    left: 0,
    top: 0,
    width: isZoomDisabled ? imageWidth : windowWidth,
    height: isZoomDisabled ? imageHeight : windowHeight,
    imageWidth,
    imageHeight,
    windowWidth,
    windowHeight,
    isZoomed: false,
    isZoomDisabled,
    lastPanEvent: null,
    lastPinchEvent: null
  };
};

class Image extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.state = computeDefaults({ image: props.image, window });
  }

  onResize = _.debounce(() => {
    this.setState(computeDefaults({ image: this.props.image, window }));
  }, 50);

  toggleZoom = () => {
    const { image } = this.props;
    const { isZoomDisabled, isZoomed } = this.state;

    if (isZoomDisabled) {
      return;
    }

    if (isZoomed) {
      this.setState(computeDefaults({ image, window }));
      return;
    }

    this.setState(prevState => zoomToCenter(prevState));
  };

  onTap = event => {
    if (event.pointerType === 'touch') {
      return;
    }

    this.toggleZoom();
  };

  onDoubleTap = event => {
    if (event.pointerType !== 'touch') {
      return;
    }

    this.toggleZoom();
  };

  onPanStart = event => {
    if (this.state.isZoomed) {
      this.setState({ lastPanEvent: event });
    }
  };

  onPanMove = event => {
    if (this.state.isZoomed) {
      this.setState(prevState => ({
        ...pan(prevState, event),
        lastPanEvent: event
      }));
    }
  };

  onPanEnd = () => {
    if (this.state.isZoomed) {
      this.setState({ lastPanEvent: null });
    }
  };

  onPinchStart = event => {
    if (!this.state.isZoomDisabled) {
      this.setState({ lastPinchEvent: event });
    }
  };

  onPinchEnd = () => {
    if (!this.state.isZoomDisabled) {
      this.setState({ lastPinchEvent: null });
    }
  };

  onPinch = event => {
    if (!this.state.isZoomDisabled) {
      this.setState(prevState => ({
        ...pinch(prevState, event),
        lastPinchEvent: event
      }));
    }
  };

  componentDidMount() {
    const el = this.ref.current;
    this.hammertime = new Hammer(el);
    this.hammertime.get('pinch').set({ enable: true });
    this.hammertime.on('tap', this.onTap);
    this.hammertime.on('doubletap', this.onDoubleTap);
    this.hammertime.on('panstart', this.onPanStart);
    this.hammertime.on('panmove', this.onPanMove);
    this.hammertime.on('panend', this.onPanEnd);
    this.hammertime.on('pinchstart', this.onPinchStart);
    this.hammertime.on('pinchend', this.onPinchEnd);
    this.hammertime.on('pinch', this.onPinch);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  componentWillUnmount() {
    this.hammertime.off('tap', this.onTap);
    this.hammertime.off('tap', this.onTap);
    this.hammertime.off('doubletap', this.onDoubleTap);
    this.hammertime.off('panstart', this.onPanStart);
    this.hammertime.off('panmove', this.onPanMove);
    this.hammertime.off('panend', this.onPanEnd);
    this.hammertime.off('pinchstart', this.onPinchStart);
    this.hammertime.off('pinchend', this.onPinchEnd);
    this.hammertime.off('pinch', this.onPinch);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
  }

  render() {
    const { url } = this.props;
    const {
      left,
      top,
      width,
      height,
      windowWidth,
      windowHeight,
      isZoomDisabled,
      isZoomed
    } = this.state;

    const cursor = isZoomed ? 'zoom-out' : 'zoom-in';

    return (
      <div
        ref={this.ref}
        id="zoom-image"
        className="image-zoom__image"
        style={{
          backgroundImage: `url(${url})`,
          left: isZoomDisabled ? (windowWidth - width) / 2 : -1 * left,
          top: isZoomDisabled ? (windowHeight - height) / 2 : -1 * top,
          width,
          height,
          cursor: isZoomDisabled ? '' : cursor
        }}
      />
    );
  }
}

const ImageZoomModal = props => {
  const { url, history, match } = props;

  const onClose = () => {
    history.push(`/${match.params.pathname}`);
  };

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={onClose}
      className="reveal full reveal--no-overflow"
      overlayClassName="reveal-overlay"
      appElement={document.getElementById('byo-web-ui')}>
      <div className="modal zoom-modal">
        <ModalHeader
          onClose={onClose}
          analyticsKey="enlarge close"
          className="modal-header--overlay"
        />
        <div>
          <div className="image-zoom__container">
            <LoadImage url={url}>
              {({ isLoading, image }) => {
                if (isLoading) {
                  return (
                    <SvgIcon
                      name="spinner"
                      className="image-zoom__loading-icon"
                    />
                  );
                }
                return <Image image={image} url={url} />;
              }}
            </LoadImage>
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

const mapPropsToState = (state, ownProps) => {
  const { match, onClose } = ownProps;
  const { type, index } = match.params;
  const { config, appResources, cgi, breakpoint } = state;
  const images = normalize(
    appResources,
    config,
    cgi.isConvertibleTopDown,
    breakpoint
  );
  const url = _.get(images, [type, index, 'zoomUrl']);
  return { url, onClose };
};

export default withRouter(connect(mapPropsToState)(ImageZoomModal));
